import { useEntityConflict } from '@tunasong/graph-lib/react'
import { useSelector } from '@tunasong/redux'
import type { FC, ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useActionToast } from '../alert/use-action-toast.js'
import { ConflictBanner } from './conflict-banner.js'
import type { ConflictBannerProps } from './conflict-banner.js'
import { conflictNoticeCopy as copy } from './conflict-notice-copy.js'
import { conflictPanels, takeFocusRequest } from './conflict-panels.js'
import type { ConflictScope } from './conflict-panels.js'
import styles from './conflict-banner.module.css'

export interface EntityConflictBannerProps {
  entityId?: string
  /** The fields the hosting panel edits; the banner lists only those, and shows nothing when none is held */
  fields?: readonly string[]
  /** Or: every held field except these, which other panels own (the settings sidebar) */
  exclude?: readonly string[]
  /** Take focus on mount (the Review dialog); a panel Review opens takes focus through the request instead */
  focus?: ConflictBannerProps['focus']
  /** Wrap the banner, such as in the Review dialog; the status line and the Undo snackbar stay outside it */
  wrap?: (banner: ReactNode) => ReactNode
  /** After Keep my changes or Use their version */
  onResolved?: () => void
  /** Name the banner "Editing conflict"; off where a dialog title already says it (GH #2713). @default true */
  labelled?: boolean
}

const focusable = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/** The panel's first field after the banner: where focus goes when the banner it was in closes (GH #2713) */
const firstFocusableAfter = (anchor: HTMLElement) =>
  Array.from(anchor.parentElement?.querySelectorAll<HTMLElement>(focusable) ?? []).find(
    element => !anchor.contains(element)
  ) ?? null

/**
 * The conflict notice for one entity, wired to its held conflict (GH #1912 option B,
 * docs/ux/2026-10-24-conflict-notice-ux.md, Behaviour). Mount it at the top of a panel that edits structured fields:
 *   - "Keep my changes" saves mine, the banner closes and "Your changes are saved." is announced;
 *   - "Use their version" shows theirs, with an Undo snackbar that saves mine after all.
 * Both resolve only the fields this banner shows. While mounted it counts as showing them, so the app-level snackbar
 * leaves them out; when Review opened its panel, it takes focus.
 */
export const EntityConflictBanner: FC<EntityConflictBannerProps> = props => {
  const { entityId, fields, exclude, focus, wrap, onResolved, labelled } = props
  const heldFields = useSelector(state => (entityId ? state.entities.conflicts[entityId]?.fields : undefined))
  const scopeFields =
    fields ?? (exclude && heldFields ? heldFields.filter(field => !exclude.includes(field)) : undefined)
  const { conflict, keepMine, takeTheirs, undoTakeTheirs } = useEntityConflict(entityId, scopeFields)
  const { showToast, toast } = useActionToast()
  const [kept, setKept] = useState(false)
  const [requestedFocus, setRequestedFocus] = useState<ConflictBannerProps['focus']>()
  const key = useId()
  /** Inline only: the banner's place in its panel, and where focus goes once it closes with focus inside it */
  const anchorRef = useRef<HTMLDivElement>(null)
  const focusAfterRef = useRef<HTMLElement | null>(null)

  const scope: ConflictScope = fields ? { fields } : { exclude: exclude ?? [] }
  const scopeKey = JSON.stringify(scope)

  useEffect(() => {
    if (!entityId) {
      return
    }
    return conflictPanels.register({ key, entityId, scope: JSON.parse(scopeKey) as ConflictScope, visible: true })
  }, [key, entityId, scopeKey])

  const hasConflict = Boolean(conflict)
  useEffect(() => {
    if (hasConflict && entityId && takeFocusRequest(entityId, JSON.parse(scopeKey) as ConflictScope)) {
      setRequestedFocus('region')
    }
  }, [hasConflict, entityId, scopeKey])

  /** GH #2713: the banner closed with focus inside it, which would drop focus to the page; the panel's field takes it */
  useEffect(() => {
    const target = focusAfterRef.current
    if (hasConflict || !target) {
      return
    }
    focusAfterRef.current = null
    if (target.isConnected) {
      target.focus()
    }
  }, [hasConflict])

  /** GH #2713 P2: while the toast shows, the off-screen snackbar holds back, so the two don't stack */
  const toastShowing = Boolean(toast)
  useEffect(() => {
    if (!toastShowing || !entityId) {
      return
    }
    return conflictPanels.register({ key: `${key}:toast`, entityId, scope: { fields: [] }, toast: true })
  }, [toastShowing, key, entityId])

  const captureFocusAfter = () => {
    const anchor = anchorRef.current
    if (anchor?.contains(document.activeElement)) {
      focusAfterRef.current = firstFocusableAfter(anchor)
    }
  }

  const handleKeep = async () => {
    captureFocusAfter()
    try {
      await keepMine()
    } catch (error) {
      /** The banner stays, with Try again; nothing to hand focus to */
      focusAfterRef.current = null
      throw error
    }
    setKept(true)
    onResolved?.()
  }

  const handleTakeTheirs = () => {
    captureFocusAfter()
    const mine = takeTheirs()
    if (!mine) {
      return
    }
    showToast({ message: copy.switched, actionLabel: copy.undo, onAction: () => void undoTakeTheirs(mine) })
    onResolved?.()
  }

  const banner = conflict ? (
    <ConflictBanner
      fields={conflict.fields}
      onKeep={handleKeep}
      onTakeTheirs={handleTakeTheirs}
      focus={focus ?? requestedFocus}
      labelled={labelled}
    />
  ) : null

  return (
    <>
      {wrap ? (
        wrap(banner)
      ) : (
        <div ref={anchorRef} className={styles.contents}>
          {banner}
        </div>
      )}
      {kept && !conflict ? (
        <div role="status" className={styles.visuallyHidden}>
          {copy.kept}
        </div>
      ) : null}
      {toast}
    </>
  )
}

export default EntityConflictBanner
